// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import {
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonToggle,
  IonSpinner,
  IonModal,
  IonHeader,
  IonContent,
} from '@ionic/angular/standalone';
import { MatButtonModule } from '@angular/material/button';
import { Capacitor } from '@capacitor/core';
import { addIcons } from 'ionicons';
import { close, chevronDown, chevronBack, logOutOutline } from 'ionicons/icons';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChartBar, faFileLines, faHouse, faPlane } from '@fortawesome/pro-solid-svg-icons';
import { DataStore, NoAccessTypes } from '@core/store/data.store';
import { RootStore } from '@core/store/root.store';
import { FlightsStore } from '@features/flights/store/flights.store';
import type { DisplayableFlight } from '@shared/models/flights/flight';
import { FeatureFlagsStore } from '@core/store/feature-flags.store';
import { UiOverridesStore } from '@core/store/ui-overrides.store';
import { ALL_FEATURE_FLAGS } from '@shared/models/feature-flags';
import type { FlightTintOverride } from '@shared/models/ui-overrides';
import { ReadableOnDarkDirective } from '@shared/directives/readable-on-dark.directive';
import { SafeHtmlPipe } from '@shared/pipes/safeHtml.pipe';
import { SaraIconComponent } from '@shared/ui/sara-icon/sara-icon.component';
import { AppTheme, AppViews, ViewIcons } from '@shared/models/views';
import { TopBarComponent } from './top-bar/top-bar.component';
import { ImpersonationBannerComponent } from './impersonation-banner/impersonation-banner.component';
import { MatMenuModule } from '@angular/material/menu';
import { FlightDetailsComponent } from '@features/flights/pages/flight-details/flight-details.component';
import { GUIDED_TOUR_VERSION, TourAnchorDirective, TourHostComponent, ToursStore } from '@ergoss/guided-tour';
import type { TourDef } from '@ergoss/guided-tour';
import type { TourStudioNotice } from '@ergoss/guided-tour/studio';
import { TourStudioComponent, TourStudioStore } from '@ergoss/guided-tour/studio';
import { TOUR_BRIEFING_DETAILS_VIEW, TOUR_FLIGHT_DETAILS_VIEW, ToursService } from '@core/tour/tours.service';
import { BriefingStore } from '@features/briefing/store/briefing.store';
import { PushNotificationsService } from '@core/services/push-notifications.service';
import { ToastrService } from 'ngx-toastr';

// Escape hatch for the boot spinner. The store's 30s watchdog already flips
// SessionLoadFailed (+ Retry), but Retry re-runs the chain in-app and won't
// recover a wedged client. So after this shorter delay we surface a manual
// "Refresh" (hard page reload) under the spinner — independent of the boot
// state machine, so it appears even if the chain never advances.
const BOOT_REFRESH_DELAY_MS = 10000;

// Locked-toggle shake, in step with --shake-duration in the SCSS. On a timer and
// not on animationend, which never fires when the modal is dismissed mid-shake or
// the animation is suppressed (reduced motion, hidden tab).
const SHAKE_DURATION_MS = 320;

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  imports: [
    IonTabs,
    IonTabBar,
    IonTabButton,
    IonIcon,
    IonToggle,
    IonSpinner,
    IonModal,
    IonHeader,
    IonContent,
    ReadableOnDarkDirective,
    SafeHtmlPipe,
    SaraIconComponent,
    TopBarComponent,
    ImpersonationBannerComponent,
    MatMenuModule,
    MatButtonModule,
    FlightDetailsComponent,
    TourAnchorDirective,
    TourStudioComponent,
    TourHostComponent,
    FontAwesomeModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainComponent {
  // The app tour's last-driven view — lets the navigation effect act only on
  // changes and close the flight-detail overlay when the tour leaves it.
  private _prevTourView: string | null = null;
  // Surfaces the studio's notify output — the studio itself is
  // notification-stack-agnostic (lib code, RFC-003).
  private readonly _toastr = inject(ToastrService);
  // iOS push registration (no-ops off native iOS) — see the boot effect.
  private readonly _push = inject(PushNotificationsService);
  // Guards the push init so it fires once per app load.
  private _pushInitLaunched = false;
  private _shakeTimer: ReturnType<typeof setTimeout> | undefined;

  // Guards the landing-page auto-start so it fires once per app load.
  private _bootTourLaunched = false;

  // One row per known feature flag for the dev settings section: effective
  // state + whether a local override is active. Adapts JMO's SARASettings debug
  // panel into the settings modal.
  protected readonly featureFlagRows = computed(() => {
    const server = this.ffs.serverFlags().data ?? {};
    const overrides = this.ffs.overrides();
    return ALL_FEATURE_FLAGS.map((name) => ({
      name,
      server: server[name] ?? false,
      overridden: overrides[name] !== undefined,
      effective: this.ffs.isEnabled(name),
    }));
  });

  // Flips true once the boot spinner has been up past BOOT_REFRESH_DELAY_MS,
  // revealing a manual hard-reload escape hatch. Timer-driven from app load —
  // deliberately decoupled from the boot signal graph so it still fires if the
  // chain wedges. Only ever read inside the spinner (@default) modal arm, which
  // is unreachable after first boot, so no need to reset it.
  protected readonly bootRefreshVisible = signal(false);

  // Overview-widgets row (`${category}:${statId}`) whose locked toggle is wobbling.
  protected readonly shakenWidget = signal<string | null>(null);

  // Notifications ride the /user-config/notifications backend, which is not in
  // production yet, so the section stays behind the PilotNotifications flag —
  // off for everyone until a dev flips it from Settings → Developer. It was a
  // build-time gate (`environment.production`) before, which nobody could lift
  // on a deployed environment: a flag is what makes the screen testable on dev,
  // where the backend route lives today. Retire the flag once the PwiApi
  // release reaches production.
  //
  // The gate is the UI only: the boot still loads the prefs (DataStore, after
  // the disclaimer). Gating that read would mean DataStore injecting
  // FeatureFlagsStore, which already injects DataStore — a DI cycle. The read
  // is harmless either way: it fails silently into the defaults while the route
  // is absent, and stops failing the day it ships.
  //
  // `ffs` is declared below: read inside the computation, so it resolves on the
  // first change-detection pass rather than at field initialization.
  protected readonly notificationsDisabled = computed(() => !this.ffs.isEnabled('PilotNotifications'));

  // Header-tint choices of the Developer section's UI overrides row; null
  // follows the data again.
  protected readonly flightTintOptions: readonly { value: FlightTintOverride | null; label: string }[] = [
    { value: null, label: 'Real' },
    { value: 'no-data', label: 'No data' },
    { value: 'to-review', label: 'To review' },
    { value: 'in-sara', label: 'In SARA' },
    { value: 'no-data-yellow', label: 'Yellow' },
  ];

  readonly ds = inject(DataStore);
  readonly rs = inject(RootStore);
  readonly fs = inject(FlightsStore);
  // Briefing store — the app tour's briefing-details section drives a real
  // briefing load (placeholder route) through it; see driveTourView.
  readonly bs = inject(BriefingStore);
  readonly ffs = inject(FeatureFlagsStore);
  /** UI state overrides (dev tool) — toggled from the Developer settings section. */
  readonly uio = inject(UiOverridesStore);
  /** Tour studio (dev tool) — opened from the Developer settings section. */
  readonly tourStudio = inject(TourStudioStore);
  // Tour player — drives the overlay-host effect + the template's spotlight.
  readonly toursStore = inject(ToursStore);

  // Tour content source — read by the dev panel (content-source readout +
  // the seeding action), hence public.
  readonly toursSource = inject(ToursService);

  // Engine version, stamped into the lib at build — the dev panel shows it so
  // "which build is this app actually running?" is answerable on-device
  // (definitions come from the backend, so content and engine can drift
  // independently; today's optional-flags chase needed exactly this readout).
  readonly tourEngineVersion = GUIDED_TOUR_VERSION;

  // Enum exposures for template bindings
  noAccessTypes = NoAccessTypes;
  appViews = AppViews;
  appTheme = AppTheme;
  viewIcons = ViewIcons;

  constructor() {
    addIcons({ close, chevronDown, chevronBack, logOutOutline });
    inject(FaIconLibrary).addIcons(faHouse, faPlane, faFileLines, faChartBar);

    setTimeout(() => this.bootRefreshVisible.set(true), BOOT_REFRESH_DELAY_MS);

    // Mirror device/orientation flags onto <body> classes so global SCSS can
    // react with :host-context(.portrait) / .desktop / etc. Pure DOM concern.
    effect(() => {
      document.body.classList.toggle('portrait', this.rs.isPortrait());
      document.body.classList.toggle('landscape', !this.rs.isPortrait());
      document.body.classList.toggle('small-tablet', this.rs.isSmallTablet());
      document.body.classList.toggle('small-device', this.rs.isSmallDevice());
      // iPad Mini only — home stacks its dashboard into a column even in
      // landscape (see :host-context(.mini-tablet) in home.component.scss).
      document.body.classList.toggle('mini-tablet', this.rs.isMiniTablet());
      document.body.classList.toggle('desktop', this.rs.isDesktop());
      // Width-driven (≤ 1024px), independent of physical orientation: pages
      // react with :host-context(.narrow) to stack their layout into a column.
      document.body.classList.toggle('narrow', this.rs.narrowViewport());
      // Real iOS device only (native status bar present). NOT the Ionic `.ios`
      // mode class — provideIonicAngular({ mode: 'ios' }) forces that on every
      // platform incl. web, so it's not a device signal. Global SCSS reacts with
      // body.ios-device to add the status-bar safe-area offset (see styles.scss).
      document.body.classList.toggle('ios-device', this.rs.isIos());
    });

    // ── Guided-tour: drive the app-wide tour's navigation ────────────────
    // The single navigating tour tags each step with a `view`; the shell takes
    // the tour to that screen before the step shows — a tab switch for the four
    // main views, and for the flight-detail section it opens the first flight
    // over the Flights tab (the mock's route:'/flights/1'). currentStepView is
    // memoized on the string, so this fires only on a real view change. Per-page
    // tours don't navigate → it's null → no-op. untracked: navigateTo /
    // startEditing mutate state this effect must not track.
    effect(() => {
      const view = this.toursStore.currentStepView();
      untracked(() => this.driveTourView(view));
    });

    // ── iOS push notifications: register once the session is usable ──────
    // Native-iOS only (the service no-ops elsewhere). After canAccessApp so
    // the registration call carries the Bearer token + customer context the
    // interceptor injects. Permission prompt appears here, once per install.
    effect(() => {
      if (!this.ds.canAccessApp() || this._pushInitLaunched) return;
      this._pushInitLaunched = true;
      untracked(() => void this._push.init());
    });

    // ── Guided-tour: auto-run ONE tour, chosen by the LANDING page ───────
    // Fires once per app load (a reload re-runs it fresh). The landing page
    // decides which tour: Home → the full app-wide tour; any other page
    // (deep-link / reload straight onto Flights, a flight's detail, Briefing,
    // Reports) → just that page's tour — so a deep-link never drags the pilot
    // back to Home for the whole storyline. Gated like the old per-page
    // directive: flag on (decision stays in the view layer), session usable
    // (never under the boot modal), definitions loaded AND seen-state settled
    // (the per-crew map from PwiApi) — autoStart no-ops before seenLoaded, so
    // latching without it would consume this effect's one shot on a no-op and
    // the first-visit tour would never fire. Test mode (dev-panel override)
    // ignores the persisted seen-state so it re-offers every reload, and also
    // bypasses the pilot-eligibility gate so a developer can preview the tour.
    // Outside test mode only a genuine pilot is auto-offered the tour: internal
    // / non-pilot sessions (Ergoss staff, impersonation, no crew code) are never
    // auto-played — the "?" button (restartTour) stays their manual entry.
    effect(() => {
      if (this._bootTourLaunched) return;
      if (!(this.ds.canAccessApp() && this.toursStore.toursLoaded() && this.toursStore.seenLoaded())) return;
      untracked(() => {
        this._bootTourLaunched = true;
        if (!this.isPilotTourEligible()) return;
        // Seen-state is honoured for everyone now that the tours ship on: a tour
        // auto-plays once and then stays quiet. The old dev bypass forced
        // ignoreSeen whenever the GuidedTours override was on, which made the
        // "already seen" path impossible to exercise. Replay is the "?" button.
        this.toursStore.autoStart(this.bootTourId());
      });
    });
  }

  // A disabled toggle gives no feedback at all, so a tap on a locked one wobbles it.
  protected shakeLockedWidget(category: string, statId: string): void {
    clearTimeout(this._shakeTimer);
    this.shakenWidget.set(`${category}:${statId}`);
    this._shakeTimer = setTimeout(() => this.shakenWidget.set(null), SHAKE_DURATION_MS);
    // The same answer under the finger, on demand behind the platform guard like
    // the other native plugins (the web fallback, navigator.vibrate, is absent on
    // desktop and in iPad Safari anyway).
    if (Capacitor.isNativePlatform()) {
      void import('@capacitor/haptics').then((h) => h.Haptics.impact({ style: h.ImpactStyle.Medium }));
    }
  }

  // Hard reload — re-bootstraps Angular from scratch. The robust escape from a
  // wedged boot (stale signal graph, dead SignalR transport) where the in-app
  // retrySessionLoad() can't recover.
  protected reloadApp(): void {
    window.location.reload();
  }

  // Studio feedback → the app's toast stack.
  protected studioNotify(notice: TourStudioNotice): void {
    if (notice.severity === 'success') this._toastr.success(notice.message);
    else this._toastr.error(notice.message);
  }

  // The studio's Save output → PwiApi admin PUT + verify-read. On a confirmed
  // persist, reload the engine's definitions so the saved tour is live content
  // immediately (and the dev-panel content-source readout stays truthful) —
  // no app reload needed to see it in the "?" flow.
  protected saveStudioTour(def: TourDef): void {
    this.toursSource.publishTour(def).subscribe({
      next: ({ persisted }) => {
        if (persisted) {
          this._toastr.success(`Tour '${def.id}' v${def.version} saved — the backend now serves it`);
          this.toursStore.loadTours();
        } else {
          this._toastr.error(
            `Save accepted but the backend doesn't serve '${def.id}' — its _CONFIG store looks unwired (ask HDA)`,
          );
        }
      },
      error: () => this._toastr.error('Save failed — needs the admin role (not impersonating) and a migrated _CONFIG'),
    });
  }

  // Dev-only (Developer settings): seeds/repairs the backend tour store with
  // the app's BUILT-IN catalog (LOCAL_TOURS — NOT the studio draft, which has
  // no backend save yet), then reports what GET /tours actually serves.
  // served < published exposes the states that otherwise 200 silently
  // (unwired _CONFIG drops writes); an error is a missing migration, no admin
  // role, or impersonation (the admin PUT rejects it).
  protected publishTours(): void {
    this.toursSource.publishTours().subscribe({
      next: ({ published, served }) => {
        if (served >= published) {
          this._toastr.success(`Seeded the ${published} built-in tours — the backend now serves ${served}`);
        } else {
          this._toastr.error(
            `Backend accepted the ${published} writes but still serves ${served} — its _CONFIG store looks unwired (ask HDA)`,
          );
        }
      },
      error: () =>
        this._toastr.error(
          'Seeding failed — needs the admin role (not impersonating) and a migrated _CONFIG (see network tab)',
        ),
    });
  }

  // Dev-only (Developer settings): full-chain push check — PwiApi pushes a
  // test notification to this crew's registered devices. Works from any
  // platform; the notification lands on the registered iPad(s).
  protected sendTestPush(): void {
    this._push.sendTest().then(
      () => this._toastr.success('Test push requested — check the registered iPad(s)'),
      () => this._toastr.error('Test push failed — see the network tab'),
    );
  }

  // Takes the app tour to the view its current step lives on. The four main
  // views are tabs (navigateTo); flight-detail is an overlay (land on Flights,
  // open the first flight); briefing-details is the briefing page's LOADED
  // state (submit a placeholder-route briefing — the pilot's most recent
  // flight, the same derivation as briefing-input's field hints). Leaving
  // either special view (or the tour ending) restores the clean state.
  private driveTourView(view: string | null): void {
    if (view === this._prevTourView) return;
    const leavingFlightDetails = this._prevTourView === TOUR_FLIGHT_DETAILS_VIEW && view !== TOUR_FLIGHT_DETAILS_VIEW;
    const leavingBriefingDetails =
      this._prevTourView === TOUR_BRIEFING_DETAILS_VIEW && view !== TOUR_BRIEFING_DETAILS_VIEW;
    // Back to the route form (walking Back, or the tour ending here) — the
    // reset flips showBriefing off, no navigation involved.
    if (leavingBriefingDetails) this.bs.resetBriefingData();

    // A fleet with the briefing section admin-disabled renders only the
    // restricted notice — no anchor ever mounts, the view never "arrives", and
    // the walk would narrate both briefing sections as centred cards over a
    // page showing none of it. Declare them undrivable instead (lib skipView):
    // the pointer re-anchors past the sections. Deliberately NO navigation and
    // NO _prevTourView update — the next call drives the following view with
    // the leaving-cleanup for the previous one intact.
    if ((view === AppViews.briefing || view === TOUR_BRIEFING_DETAILS_VIEW) && !this.bs.briefingAccessAllowed()) {
      this.toursStore.skipView(AppViews.briefing);
      this.toursStore.skipView(TOUR_BRIEFING_DETAILS_VIEW);
      return;
    }

    if (view === TOUR_FLIGHT_DETAILS_VIEW) {
      if (this.rs.currentView() !== AppViews.flights) this.rs.navigateTo(AppViews.flights);
      if (!this.fs.selectedFlightMlId()) {
        const first = this.fs.firstFlight();
        if (first) this.fs.startEditing(first);
        // No flight to open (brand-new pilot, empty fleet) — the overlay never
        // shows, so the section is undrivable and its steps would all narrate
        // centred. Skip it; the walk continues at the briefing section.
        else this.toursStore.skipView(TOUR_FLIGHT_DETAILS_VIEW);
      }
      // The section's steps anchor on the full-screen overlay (its header chips
      // live in the top-bar). When the flights page would embed the flight
      // instead (split view), pin it full screen for the walk.
      if (this.fs.detailEmbedded()) this.fs.openDetailFullscreen();
    } else if (view === TOUR_BRIEFING_DETAILS_VIEW) {
      if (this.rs.currentView() !== AppViews.briefing) this.rs.navigateTo(AppViews.briefing, leavingFlightDetails);
      if (!this.bs.showBriefing()) {
        // Index access is typed present (noUncheckedIndexedAccess is off); an empty list yields undefined.
        const recent = this.fs.scrollableList()[0]?.flights[0] as DisplayableFlight | undefined;
        this.bs.updateBriefingRequest({
          fromIcao: recent?.originAirportIcao || 'LFPO',
          toIcao: recent?.destinationAirportIcao || 'LFMN',
        });
        this.bs.loadBriefingDetails();
      }
    } else if (view !== null && this.rs.currentView() !== view) {
      // One of the four main tab views (its string equals the tab name). When we
      // are leaving the flight-details overlay, this ONE navigation also closes
      // it by dropping `?id=`; the flights store's URL reconciler then calls
      // stopEditing() itself. Clearing the id in a separate navigation would
      // race this one — the loser is cancelled, 'preserve' carries the stale id
      // onto the new tab, and the reconciler re-opens the flight we just left.
      this.rs.navigateTo(view as AppViews, leavingFlightDetails);
    } else if (leavingFlightDetails) {
      // No tab change to carry it: the tour ended, or the next step lives on the
      // Flights tab the overlay sits over. Close it directly.
      this.fs.stopEditing();
    }
    this._prevTourView = view;
  }

  // The tour to auto-start for the page the app booted on. Home is the entry
  // point → the full app-wide tour; every other landing view → just that
  // page's tour (a flight open via ?id= → the flight-detail tour). The tour
  // ids equal the view names, except Home ('app') and the flight overlay. A
  // flight embedded by the split view is still the Flights page: its own tour.
  private bootTourId(): string {
    const view = this.rs.currentView();
    if (view === AppViews.home) return 'app';
    if (view === AppViews.flights && this.fs.detailOverlayShowing()) return TOUR_FLIGHT_DETAILS_VIEW;
    return view;
  }

  // A genuine pilot who should be auto-offered the guided tour on connection.
  // Internal / non-pilot sessions are excluded (they can still launch it from
  // the "?" button, which goes through restartTour and ignores this):
  //   - Ergoss staff — a super-user, or an @ergoss.fr login (the gateway flags
  //     @ergoss.fr as super-user, but we also match the email directly so the
  //     gate holds even where the flag isn't set);
  //   - an impersonated session (acting as a pilot who isn't the caller);
  //   - a crew-code-less account (a domain-less login on the fallback tenant).
  private isPilotTourEligible(): boolean {
    const user = this.ds.user().data;
    if (user?.isSuperUser === true) return false;
    if (this.ds.userName().toLowerCase().endsWith('@ergoss.fr')) return false;
    if (this.ds.isImpersonating()) return false;
    if (!this.ds.userCode()) return false;
    return true;
  }
}
